import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import {
  useVendorPaymentAccount,
  useVendorPaymentAccountLoginLink,
  useVendorPaymentStatus,
  useVendorPortalAccess,
  useVendorProfile,
  useVendorTaxProfile,
  useRefreshVendorPaymentAccount,
  useStartVendorPaymentAccount,
  useSubmitCASelfDeclaration,
  useSubmitNR301,
  useSubmitVendorW9,
  useUpdateVendorTaxProfile,
  useUpdateVendorProfile,
} from '@api/hooks/useVendorPortal';
import { CASelfDeclarationForm } from '@components/VendorPortal/CASelfDeclarationForm';
import { NR301Form } from '@components/VendorPortal/NR301Form';
import { useCurrentSubscription } from '@api/hooks/useSubscription';
import Button from '@components/common/Button';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type {
  UpdateVendorProfileRequest,
  VendorPaymentAccountResponse,
  VendorPaymentStatusResponse,
  VendorProfileResponse,
  UpdateVendorTaxProfileRequest,
} from '@schemas/api';
import { buildVendorTaxIdRows, buildVendorTaxIdsFromRows, W9_TAX_ID_OPTIONS } from '@/lib/vendorTaxIds';

const VENDOR_ENTITY_TYPE_OPTIONS = [
  { id: '', label: 'Unknown' },
  { id: 'individual', label: 'Individual' },
  { id: 'sole_proprietor', label: 'Sole proprietor' },
  { id: 'c_corporation', label: 'C corporation' },
  { id: 's_corporation', label: 'S corporation' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'llc', label: 'LLC' },
  { id: 'trust_estate', label: 'Trust/estate' },
  { id: 'nonprofit', label: 'Nonprofit' },
  { id: 'foreign', label: 'Foreign' },
  { id: 'other', label: 'Other' },
];

// ─── Shared: company info form fields ────────────────────────────────────────

type CompanyFormFields = {
  legal_name?: string | null;
  company_name?: string | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  address?: string | null;
  currency_preference?: string | null;
  default_payment_terms?: string | null;
};

interface CompanyInfoFormProps {
  form: CompanyFormFields;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requireLegalName?: boolean;
}

function CompanyInfoForm({ form, onChange, requireLegalName = false }: CompanyInfoFormProps) {
  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white';

  return (
    <>
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Company Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Legal Name {requireLegalName && <span className="text-red-500">*</span>}
            </label>
            <input name="legal_name" type="text" required={requireLegalName} value={form.legal_name ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
            <input name="company_name" type="text" value={form.company_name ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
            <input name="website" type="url" value={form.website ?? ''} onChange={onChange} className={inputClass} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input name="email" type="email" value={form.email ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input name="phone" type="tel" value={form.phone ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile</label>
            <input name="mobile" type="tel" value={form.mobile ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
            <input name="address" type="text" value={form.address ?? ''} onChange={onChange} className={inputClass} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
            <input name="currency_preference" type="text" placeholder="USD" value={form.currency_preference ?? ''} onChange={onChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Payment Terms</label>
            <input name="default_payment_terms" type="text" placeholder="Net 30" value={form.default_payment_terms ?? ''} onChange={onChange} className={inputClass} />
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Vendor payout status section ────────────────────────────────────────────

function PaymentStatusCard({ ps }: { ps: VendorPaymentStatusResponse }) {
  const isReady = ps.status === 'ready';

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-medium text-gray-900">{ps.customer_name}</p>
        {isReady && (
          <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
            <CheckCircle className="h-3.5 w-3.5" /> Ready
          </span>
        )}
      </div>

      {!isReady && (
        <p className="text-gray-600">
          Status: <span className="font-medium">{ps.status.replace(/_/g, ' ')}</span>
        </p>
      )}

      {ps.requirements_currently_due.length > 0 && (
        <p className="text-amber-700">Requirements due: {ps.requirements_currently_due.join(', ')}</p>
      )}

      {!ps.is_linked_to_customer && ps.recipient_account_id && (
        <p className="text-gray-500 text-xs">
          Your Stripe account is connected. This customer has not linked it for payouts yet.
        </p>
      )}

      {!ps.recipient_account_id && (
        <p className="text-gray-500 text-xs">
          Connect Stripe once from this page. Customers can then link your payout account without waiting on a separate onboarding flow.
        </p>
      )}
    </div>
  );
}

function VendorStripeAccountCard({ account }: { account?: VendorPaymentAccountResponse }) {
  const startAccount = useStartVendorPaymentAccount();
  const refreshAccount = useRefreshVendorPaymentAccount();
  const loginLink = useVendorPaymentAccountLoginLink();
  const isReady = account?.status === 'ready';

  const handleStart = async () => {
    const result = await startAccount.mutateAsync({ return_path: '/vendor-profile' });
    if (result.onboarding_url) {
      window.location.assign(result.onboarding_url);
    }
  };

  const handleManage = async () => {
    const { url } = await loginLink.mutateAsync();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Stripe Payout Account</h2>
          <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
            Connect Stripe once for this vendor account. Linked customers can then attach that account for payment transactions.
          </p>
        </div>
        {account?.recipient_account_id && (
          <Button
            type="button"
            variant="secondary"
            disabled={loginLink.isPending}
            onClick={handleManage}
          >
            {loginLink.isPending ? 'Opening...' : 'Manage Payout Account'}
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-2">
        <div className="flex items-center justify-between">
          <p className="font-medium text-gray-900">Status</p>
          {isReady && (
            <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <CheckCircle className="h-3.5 w-3.5" /> Ready
            </span>
          )}
        </div>
        <p className="text-gray-600">
          {(account?.status || 'not_started').replace(/_/g, ' ')}
        </p>
        {account?.requirements_currently_due?.length ? (
          <p className="text-amber-700">Requirements due: {account.requirements_currently_due.join(', ')}</p>
        ) : null}
        {account?.masked_bank_last4 ? (
          <p className="text-gray-600">
            Bank: {account.bank_name || 'Bank account'} ending in {account.masked_bank_last4}
          </p>
        ) : null}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => refreshAccount.mutateAsync()}
            disabled={refreshAccount.isPending}
          >
            Refresh Status
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleStart}
            disabled={startAccount.isPending || isReady}
          >
            {startAccount.isPending ? 'Starting...' : isReady ? 'Stripe Connected' : 'Connect Stripe'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VendorStripeSection() {
  const { data: paymentAccount } = useVendorPaymentAccount();
  const { data: paymentStatuses, isLoading } = useVendorPaymentStatus();

  return (
    <div className="space-y-4">
      <VendorStripeAccountCard account={paymentAccount} />
      {!isLoading && paymentStatuses?.length ? (
        <div className="space-y-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Customer Link Status</h3>
            <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
              Each customer still links your payout account separately before releasing payments.
            </p>
          </div>
          {paymentStatuses.map((ps) => (
            <PaymentStatusCard key={ps.customer_tenant_id} ps={ps} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface VendorTaxProfileSectionProps {
  vendorProfile?: VendorProfileResponse;
}

const isW9DocRequest = (request: { doc_type?: string | null }) => request.doc_type === 'w9' || !request.doc_type;

function VendorTaxProfileSection({ vendorProfile }: VendorTaxProfileSectionProps) {
  const { data, isLoading } = useVendorTaxProfile();
  const updateTaxProfile = useUpdateVendorTaxProfile();
  const submitVendorW9 = useSubmitVendorW9();
  const submitCASelfDeclaration = useSubmitCASelfDeclaration();
  const submitNR301 = useSubmitNR301();
  const [form, setForm] = useState<UpdateVendorTaxProfileRequest>({});
  const [taxIdRows, setTaxIdRows] = useState<Array<{ type: string; value: string }>>([{ type: 'US_EIN', value: '' }]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (data?.profile) {
      const taxIds = data.profile.tax_ids?.length ? data.profile.tax_ids : vendorProfile?.tax_ids;
      setForm({
        legal_name: data.profile.legal_name ?? vendorProfile?.legal_name ?? '',
        business_name: data.profile.business_name ?? vendorProfile?.company_name ?? '',
        entity_type: data.profile.entity_type ?? undefined,
        tax_classification: data.profile.tax_classification ?? '',
        address: data.profile.address ?? vendorProfile?.address ?? '',
        exempt_payee_code: data.profile.exempt_payee_code ?? '',
        fatca_exemption_code: data.profile.fatca_exemption_code ?? '',
      });
      setTaxIdRows(buildVendorTaxIdRows(taxIds).filter((row) =>
        W9_TAX_ID_OPTIONS.some((option) => option.value === row.type)
      ));
      setIsDirty(false);
    }
  }, [data, vendorProfile]);

  const setField = (field: keyof UpdateVendorTaxProfileRequest, value: string) => {
    setForm((current) => ({ ...current, [field]: value || undefined }));
    setIsDirty(true);
  };

  const buildPayload = (): UpdateVendorTaxProfileRequest => {
    const taxIds = buildVendorTaxIdsFromRows(taxIdRows);
    return {
      ...form,
      tax_ids: taxIds,
    };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await updateTaxProfile.mutateAsync(buildPayload());
    setIsDirty(false);
  };

  const handleCertify = async () => {
    await submitVendorW9.mutateAsync(buildPayload());
    setIsDirty(false);
  };

  if (isLoading) {
    return <div className="p-6 text-gray-500">Loading W-9 profile...</div>;
  }

  const openRequests = data?.requests.filter((request) => request.status !== 'completed') || [];
  const openCARequest = openRequests.find((request) => request.doc_type === 'ca_self_declaration');
  const openNR301Request = openRequests.find((request) => request.doc_type === 'nr301');
  const allW9Requests = data?.requests.filter(isW9DocRequest) || [];
  const openW9Requests = openRequests.filter(isW9DocRequest);
  const profileStatus = data?.profile.w9_status || 'not_requested';
  const signedDate = data?.profile.w9_signed_at
    ? new Date(data.profile.w9_signed_at).toLocaleDateString()
    : null;
  const hasW9ProfileValues = Boolean(
    data?.profile.legal_name ||
    data?.profile.business_name ||
    data?.profile.entity_type ||
    data?.profile.tax_classification ||
    data?.profile.address ||
    data?.profile.exempt_payee_code ||
    data?.profile.fatca_exemption_code ||
    data?.profile.tax_ids?.some((taxId) => taxId.value?.trim()) ||
    profileStatus !== 'not_requested' ||
    signedDate
  );
  const shouldShowW9Fields = allW9Requests.length > 0 || hasW9ProfileValues;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tax Profile</h2>
        <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
          Complete your tax identity once. Linked customers can use it as evidence for their tax review.
        </p>
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <p>
            Status: <span className="font-medium text-gray-900">{profileStatus.replace(/_/g, ' ')}</span>
            {signedDate ? <span className="ml-2 text-gray-500">Certified {signedDate}</span> : null}
          </p>
        </div>
      </div>

      {openCARequest && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-semibold">Canadian self-declaration requested</p>
          <p className="mt-1">
            {openCARequest.customer_name || 'A customer'} has requested a Canadian
            self-declaration for {openCARequest.filing_year} on{' '}
            {new Date(openCARequest.requested_at).toLocaleDateString()}.
            {openCARequest.note ? ` Note: ${openCARequest.note}` : ''}
          </p>
          <div className="mt-4">
            <CASelfDeclarationForm
              isPending={submitCASelfDeclaration.isPending}
              onSubmit={async (values) => {
                await submitCASelfDeclaration.mutateAsync(values);
              }}
            />
          </div>
        </div>
      )}

      {openNR301Request && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900">
          <p className="font-semibold">NR301 treaty declaration requested</p>
          <p className="mt-1">
            {openNR301Request.customer_name || 'A customer'} has requested an NR301
            treaty-benefits declaration for {openNR301Request.filing_year} on{' '}
            {new Date(openNR301Request.requested_at).toLocaleDateString()}.
            {openNR301Request.note ? ` Note: ${openNR301Request.note}` : ''}
          </p>
          <div className="mt-4">
            <NR301Form
              isPending={submitNR301.isPending}
              onSubmit={async (values) => {
                await submitNR301.mutateAsync(values);
              }}
            />
          </div>
        </div>
      )}

      {openW9Requests.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">W-9 requested</p>
          <ul className="mt-2 space-y-1">
            {openW9Requests.map((request) => (
              <li key={request.id}>
                {request.customer_name || 'A customer'} requested W-9 information for {request.filing_year} on {new Date(request.requested_at).toLocaleDateString()}.
                {request.note ? ` Note: ${request.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {shouldShowW9Fields && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Legal name
              <input value={form.legal_name ?? ''} onChange={(e) => setField('legal_name', e.target.value)} className="input mt-1" />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Business name
              <input value={form.business_name ?? ''} onChange={(e) => setField('business_name', e.target.value)} className="input mt-1" />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Entity type
              <SearchableSelect
                value={form.entity_type ?? ''}
                onChange={(value) => setField('entity_type', value)}
                options={VENDOR_ENTITY_TYPE_OPTIONS}
                placeholder="Unknown"
                clearable={false}
                className="mt-1"
              />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Tax classification
              <input value={form.tax_classification ?? ''} onChange={(e) => setField('tax_classification', e.target.value)} className="input mt-1" />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 md:col-span-2">
              Address
              <input value={form.address ?? ''} onChange={(e) => setField('address', e.target.value)} className="input mt-1" />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Exempt payee code
              <input value={form.exempt_payee_code ?? ''} onChange={(e) => setField('exempt_payee_code', e.target.value)} className="input mt-1" />
            </label>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              FATCA exemption code
              <input value={form.fatca_exemption_code ?? ''} onChange={(e) => setField('fatca_exemption_code', e.target.value)} className="input mt-1" />
            </label>
            <div className="space-y-2 md:col-span-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Taxpayer ID</p>
              {taxIdRows.map((row, index) => (
                <div key={`${row.type}-${index}`} className="grid grid-cols-1 gap-2 md:grid-cols-[180px_1fr_auto]">
                  <SearchableSelect
                    value={row.type}
                    onChange={(selectedValue) => {
                      const next = [...taxIdRows];
                      next[index] = { ...next[index], type: selectedValue };
                      setTaxIdRows(next);
                      setIsDirty(true);
                    }}
                    options={W9_TAX_ID_OPTIONS.map((option) => ({ id: option.value, label: option.label }))}
                    placeholder="Tax ID type"
                    clearable={false}
                  />
                  <input
                    value={row.value}
                    onChange={(event) => {
                      const next = [...taxIdRows];
                      next[index] = { ...next[index], value: event.target.value };
                      setTaxIdRows(next);
                      setIsDirty(true);
                    }}
                    className="input"
                    placeholder="12-3456789"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setTaxIdRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
                      setIsDirty(true);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setTaxIdRows((current) => [...current, { type: 'US_EIN', value: '' }]);
                  setIsDirty(true);
                }}
              >
                Add Tax ID
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
            <Button type="submit" variant="primary" disabled={!isDirty || updateTaxProfile.isPending}>
              {updateTaxProfile.isPending ? 'Saving...' : 'Save W-9 Profile'}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={submitVendorW9.isPending}
              onClick={handleCertify}
            >
              {submitVendorW9.isPending ? 'Submitting...' : 'Certify and Submit W-9'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Vendor-tier view ─────────────────────────────────────────────────────────

function VendorCompanyProfile() {
  const { data: profile, isLoading } = useVendorProfile();
  const updateProfile = useUpdateVendorProfile();

  const [form, setForm] = useState<UpdateVendorProfileRequest>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        legal_name: profile.legal_name,
        company_name: profile.company_name ?? '',
        website: profile.website ?? '',
        email: profile.email ?? '',
        phone: profile.phone ?? '',
        mobile: profile.mobile ?? '',
        address: profile.address ?? '',
        currency_preference: profile.currency_preference ?? '',
        default_payment_terms: profile.default_payment_terms ?? '',
        is_1099: profile.is_1099 ?? false,
      });
      setIsDirty(false);
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
    setIsDirty(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpdateVendorProfileRequest = {};
    for (const [key, val] of Object.entries(form)) {
      if (val !== '' && val !== null && val !== undefined) {
        (payload as Record<string, unknown>)[key] = val;
      }
    }
    await updateProfile.mutateAsync(payload);
    setIsDirty(false);
  };

  if (isLoading) return <div className="p-6 text-gray-500">Loading profile...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">Company Profile</h1>
        <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
          Changes are automatically synced to all connected customers.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-surface-base-dark rounded-lg border border-border-default p-6 space-y-6">
        <CompanyInfoForm form={form} onChange={handleChange} requireLegalName />

        <section>
          <div className="flex items-center gap-3">
            <input
              name="is_1099"
              type="checkbox"
              id="is_1099"
              checked={form.is_1099 ?? false}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="is_1099" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              1099 Vendor
            </label>
          </div>
        </section>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="submit" variant="primary" disabled={!isDirty || updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>

      <div className="bg-white dark:bg-surface-base-dark rounded-lg border border-border-default p-6">
        <VendorTaxProfileSection vendorProfile={profile} />
      </div>

      <div className="bg-white dark:bg-surface-base-dark rounded-lg border border-border-default p-6">
        <VendorStripeSection />
      </div>
    </div>
  );
}

// ─── Router: wait for subscription and vendor access, then mount the vendor view ─────────────────

export default function VendorProfilePage() {
  const { data: subscription, isLoading: isLoadingSubscription } = useCurrentSubscription();
  const { data: vendorPortalAccess, isLoading: isLoadingVendorAccess } = useVendorPortalAccess();

  if (isLoadingSubscription || isLoadingVendorAccess) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  const isVendorTier = subscription?.plan?.features?.vendor_profile === true;
  const hasVendorCustomerLinks = vendorPortalAccess?.has_vendor_access === true;

  if (!isVendorTier && !hasVendorCustomerLinks) {
    return <Navigate to="/" replace />;
  }

  return <VendorCompanyProfile />;
}
